import React, { useEffect, useRef, useState } from "react";
import { G, UI, useStore, isGM, controlsObj, currentPageId, openWindow, type Tool } from "../store";
import { send, update, create, remove, onFx, localLocks, setGame, toast } from "../net";
import type { Obj, Page, Layer } from "../../shared/types";
import { draw, handles, doorHandleAt, measureText, EXPLORE_SCALE, type Cam, type Preview, type Ruler, type Ping } from "./render";
import { hitObj, objBounds, toLocal, simplify, pathFromPoints, center, distToSeg } from "./geometry";
import { setImageListener } from "./images";
import { uploadFile } from "../api";
import { prompt } from "../util";

const cams = new Map<string, Cam>();
const explored = new Map<string, HTMLCanvasElement>();
let clipboard: Obj[] = [];

// ---- undo (GM/object edits made from the board) ----
type UndoEntry = { id: string; before: Obj | null; after: Obj | null }[];
const undoStack: UndoEntry[] = [];
export function pushUndo(e: UndoEntry) {
  undoStack.push(e);
  if (undoStack.length > 100) undoStack.shift();
}
async function undo() {
  const e = undoStack.pop();
  if (!e) return;
  for (const { id, before, after } of e.reverse()) {
    if (!before && after) remove("obj", id);
    else if (before && !after) {
      const { id: _i, ...data } = before;
      const nid = await create("obj", data);
      // later entries may reference the old id
      for (const u of undoStack) for (const x of u) if (x.id === id) x.id = nid;
    } else if (before) update("obj", id, { x: before.x, y: before.y, w: before.w, h: before.h, rot: before.rot, pts: before.pts, layer: before.layer, z: before.z, fontSize: before.fontSize });
  }
}

function exploredCanvas(page: Page, gameId: string): HTMLCanvasElement {
  const key = gameId + ":" + page.id;
  const w = Math.ceil(page.width * page.grid * EXPLORE_SCALE), h = Math.ceil(page.height * page.grid * EXPLORE_SCALE);
  let c = explored.get(key);
  if (!c || c.width !== w || c.height !== h) {
    c = document.createElement("canvas");
    c.width = w; c.height = h;
    explored.set(key, c);
    try {
      const saved = localStorage.getItem("hearth_explored:" + key);
      if (saved) {
        const im = new Image();
        im.onload = () => {
          const g = c!.getContext("2d")!;
          g.save();
          g.setTransform(1, 0, 0, 1, 0, 0);
          g.globalCompositeOperation = "source-over";
          g.drawImage(im, 0, 0);
          g.restore();
        };
        im.src = saved;
      }
    } catch {}
  }
  return c;
}
export function resetExplored(pageId: string) {
  const key = G.get().game?.id + ":" + pageId;
  const c = explored.get(key);
  c?.getContext("2d")!.clearRect(0, 0, c.width, c.height);
  try { localStorage.removeItem("hearth_explored:" + key); } catch {}
}

type Drag =
  | { kind: "pan"; sx: number; sy: number; cx: number; cy: number; moved: boolean; right: boolean; w: { x: number; y: number } }
  | { kind: "move"; sw: { x: number; y: number }; starts: Obj[]; last: number; moved: boolean }
  | { kind: "resize"; h: string; start: Obj }
  | { kind: "rotate"; start: Obj }
  | { kind: "vertex"; start: Obj; idx: number }
  | { kind: "marquee"; x: number; y: number }
  | { kind: "draw"; pts: number[] }
  | { kind: "ruler"; x: number; y: number; last: number }
  | { kind: "fogrect"; x: number; y: number }
  | { kind: "door"; x: number; y: number };

export function Board() {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const size = useRef({ w: 0, h: 0, dpr: 1 });
  const dirty = useRef(true);
  const drag = useRef<Drag | null>(null);
  const preview = useRef<Preview | null>(null);
  const draft = useRef<number[]>([]); // poly / wall points in progress
  const rulers = useRef(new Map<string, Ruler & { t: number }>());
  const pings = useRef<Ping[]>([]);
  const mouse = useRef({ x: 0, y: 0, sx: 0, sy: 0 });
  const longPress = useRef<any>(null);
  const spaceDown = useRef(false);
  const hoverDoor = useRef<string | null>(null);
  const visibleFn = useRef<(x: number, y: number) => boolean>(() => true);
  const [menu, setMenu] = useState<{ x: number; y: number; id: string | null; wx: number; wy: number } | null>(null);
  const pageId = useStore(UI, () => currentPageId());
  useStore(G, s => s.game?.state.playerPage);

  const invalidate = () => { dirty.current = true; };

  const page = (): Page | null => G.get().recs.page[currentPageId()] ?? null;
  const cam = (): Cam => {
    const p = page();
    if (!p) return { x: 0, y: 0, z: 1 };
    let c = cams.get(p.id);
    if (!c && !size.current.w) return { x: 0, y: 0, z: 1 };
    if (!c) {
      const { w, h } = size.current;
      const pw = p.width * p.grid, ph = p.height * p.grid;
      const z = Math.max(0.15, Math.min(1.2, Math.min(w / pw, h / ph) * 0.95));
      c = { z, x: pw / 2 - w / 2 / z, y: ph / 2 - h / 2 / z };
      cams.set(p.id, c);
    }
    return c;
  };
  const toWorld = (sx: number, sy: number) => {
    const c = cam();
    return { x: c.x + sx / c.z, y: c.y + sy / c.z };
  };
  const pageObjs = (): Obj[] => {
    const pid = currentPageId();
    return Object.values(G.get().recs.obj).filter(o => o.pageId === pid);
  };

  // ---- render loop ----
  useEffect(() => {
    let raf = 0;
    let lastSave = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const now = performance.now();
      pings.current = pings.current.filter(p => now - p.t0 < 1800);
      for (const [k, r] of rulers.current) if (now - r.t > 8000) rulers.current.delete(k);
      if (!dirty.current && !pings.current.length) return;
      dirty.current = false;
      const cv = canvas.current, p = page();
      if (!cv || !p || !size.current.w) return;
      const ctx = cv.getContext("2d")!;
      const s = G.get(), ui = UI.get();
      const gm = isGM();
      const exp = p.lighting && p.explorer && !gm ? exploredCanvas(p, s.game!.id) : null;
      visibleFn.current = draw(ctx, {
        page: p, objs: pageObjs(), decks: s.recs.deck as any, cam: cam(), w: size.current.w, h: size.current.h, dpr: size.current.dpr,
        gm, layer: ui.layer, tool: ui.tool, selected: new Set(ui.selected), controls: o => controlsObj(o),
        preview: preview.current, rulers: [...rulers.current.values()], pings: pings.current, gmDark: ui.gmDark,
        explored: exp, style: { stroke: ui.stroke, fill: ui.fill, strokeWidth: ui.strokeWidth }, hoverDoor: hoverDoor.current,
      });
      if (exp && now - lastSave > 5000) {
        lastSave = now;
        try { localStorage.setItem(`hearth_explored:${s.game!.id}:${p.id}`, exp.toDataURL()); } catch {}
      }
    };
    raf = requestAnimationFrame(loop);
    const u1 = G.subscribe(invalidate), u2 = UI.subscribe(invalidate);
    setImageListener(invalidate);
    return () => { cancelAnimationFrame(raf); u1(); u2(); };
  }, []);

  // ---- resize ----
  useEffect(() => {
    const el = wrap.current!;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      size.current = { w: r.width, h: r.height, dpr };
      const cv = canvas.current!;
      cv.width = Math.round(r.width * dpr);
      cv.height = Math.round(r.height * dpr);
      cv.style.width = r.width + "px";
      cv.style.height = r.height + "px";
      invalidate();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- remote fx: rulers, pings, camera pulls ----
  useEffect(() => onFx((fx, from) => {
    if (fx.pageId && fx.pageId !== currentPageId()) return;
    if (fx.kind === "ruler") {
      if (fx.clear) rulers.current.delete(from);
      else rulers.current.set(from, { pts: fx.pts, shape: fx.shape, color: fx.color, name: fx.name, t: performance.now() });
      invalidate();
    } else if (fx.kind === "ping" || fx.kind === "pull") {
      pings.current.push({ x: fx.x, y: fx.y, color: fx.color, t0: performance.now() });
      if (fx.kind === "pull") centerOn(fx.x, fx.y);
      invalidate();
    }
  }), []);

  const centerOn = (x: number, y: number) => {
    const c = cam();
    c.x = x - size.current.w / 2 / c.z;
    c.y = y - size.current.h / 2 / c.z;
    invalidate();
  };
  useEffect(() => {
    const h = (e: any) => centerOn(e.detail.x, e.detail.y);
    window.addEventListener("hearth:center", h);
    return () => window.removeEventListener("hearth:center", h);
  }, []);

  // page switch resets transient state
  useEffect(() => {
    draft.current = [];
    preview.current = null;
    rulers.current.clear();
    UI.set({ selected: [] });
    invalidate();
  }, [pageId]);

  const myColor = () => G.get().members.find(m => m.uid === G.get().me)?.color || "#f80";

  // ---- selection helpers ----
  const selectableLayer = (): Layer => (isGM() ? UI.get().layer : "objects");
  const canPick = (o: Obj) => {
    if (o.layer !== selectableLayer()) return false;
    if (!isGM()) {
      if (o.type === "image" && !visibleFn.current(o.x + o.w / 2, o.y + o.h / 2) && !controlsObj(o)) return false;
    }
    return true;
  };
  const topAt = (x: number, y: number): Obj | null => {
    const tol = 6 / cam().z;
    const objs = pageObjs().filter(canPick).sort((a, b) => b.z - a.z);
    return objs.find(o => hitObj(o, x, y, tol)) ?? null;
  };
  const movable = (o: Obj) => !o.locked && controlsObj(o) && (isGM() || o.layer === "objects");

  const snapObj = (o: Obj, p: Page): { x: number; y: number } => {
    if (!p.snap || o.type === "wall" || o.type === "path" || o.type === "text") return { x: o.x, y: o.y };
    const g = p.grid;
    if (o.w < g * 0.99 || o.h < g * 0.99) {
      const c = center(o);
      return { x: Math.floor(c.x / g) * g + (g - o.w) / 2, y: Math.floor(c.y / g) * g + (g - o.h) / 2 };
    }
    // align odd sizes by corner, keep sub-grid offsets out
    return { x: Math.round(o.x / g) * g, y: Math.round(o.y / g) * g };
  };

  const setLocal = (o: Obj) => {
    const s = G.get();
    G.set({ recs: { ...s.recs, obj: { ...s.recs.obj, [o.id]: o } } });
  };

  const broadcastRuler = (pts: number[] | null, shape: Ruler["shape"] = "line") => {
    const p = page();
    if (!p) return;
    if (!pts) send({ t: "fx", fx: { kind: "ruler", clear: true, pageId: p.id } });
    else send({ t: "fx", fx: { kind: "ruler", pageId: p.id, pts, shape, color: myColor(), name: G.get().members.find(m => m.uid === G.get().me)?.display } });
  };

  // ---- pointer handling ----
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; z: number; wx: number; wy: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    setMenu(null);
    const cv = canvas.current!;
    cv.setPointerCapture(e.pointerId);
    const r = cv.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    if (e.pointerType === "touch") {
      touches.current.set(e.pointerId, { x: sx, y: sy });
      if (touches.current.size === 2) {
        // second finger: abandon whatever the first started and pinch instead
        clearTimeout(longPress.current);
        const d0 = drag.current;
        if (d0?.kind === "move") for (const s of d0.starts) localLocks.delete(s.id);
        drag.current = null;
        preview.current = null;
        const [a, b] = [...touches.current.values()];
        const c = cam();
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y), z: c.z, wx: c.x + mx / c.z, wy: c.y + my / c.z };
        return;
      }
    }
    const w = toWorld(sx, sy);
    const ui = UI.get();
    const p = page();
    if (!p) return;
    const gm = isGM();
    clearTimeout(longPress.current);

    if (e.button === 1 || e.button === 2 || (e.button === 0 && (ui.tool === "pan" || spaceDown.current))) {
      const c = cam();
      drag.current = { kind: "pan", sx, sy, cx: c.x, cy: c.y, moved: false, right: e.button === 2, w };
      return;
    }
    if (e.button !== 0) return;
    const tool: Tool = ui.tool;

    if (tool === "select") {
      // doors
      const door = doorHandleAt(pageObjs().filter(o => o.type === "wall"), w.x, w.y, cam().z);
      if (door && (gm || p.lighting)) {
        if (gm || G.get().game?.settings.playersOpenDoors) update("obj", door.id, { open: !door.open });
        return;
      }
      // handles of single selection
      const sel = ui.selected.map(id => G.get().recs.obj[id]).filter(Boolean) as Obj[];
      if (sel.length === 1 && movable(sel[0])) {
        const o = sel[0];
        const hs = 9 / cam().z;
        if (o.type === "wall") {
          const pts = o.pts || [];
          for (let i = 0; i < pts.length; i += 2) if (Math.abs(pts[i] - w.x) < hs && Math.abs(pts[i + 1] - w.y) < hs) {
            drag.current = { kind: "vertex", start: o, idx: i };
            localLocks.add(o.id);
            return;
          }
        } else {
          for (const h of handles(o).filter(h => h.k !== "rot")) {
            if (Math.abs(h.x - w.x) < hs && Math.abs(h.y - w.y) < hs) {
              drag.current = { kind: "resize", h: h.k, start: o };
              localLocks.add(o.id);
              return;
            }
          }
          // rotation knob sits outside the box and scales with zoom
          const a = ((o.rot || 0) * Math.PI) / 180, c = center(o), off = o.h / 2 + 28 / cam().z;
          const kx = c.x + off * Math.sin(a), ky = c.y - off * Math.cos(a);
          if (Math.hypot(kx - w.x, ky - w.y) < hs) {
            drag.current = { kind: "rotate", start: o };
            localLocks.add(o.id);
            return;
          }
        }
      }
      const hit = topAt(w.x, w.y);
      longPress.current = setTimeout(() => {
        const d = drag.current;
        if (d && ((d.kind === "move" && !d.moved) || d.kind === "marquee")) {
          const pull = e.shiftKey && gm;
          const fx = { kind: pull ? "pull" : "ping", pageId: p.id, x: w.x, y: w.y, color: myColor() };
          send({ t: "fx", fx });
          pings.current.push({ x: w.x, y: w.y, color: fx.color, t0: performance.now() });
          if (d.kind === "move") for (const s of d.starts) localLocks.delete(s.id);
          drag.current = null;
          preview.current = null;
          invalidate();
        }
      }, 550);
      if (hit) {
        let selIds = ui.selected;
        if (e.shiftKey) selIds = selIds.includes(hit.id) ? selIds.filter(x => x !== hit.id) : [...selIds, hit.id];
        else if (!selIds.includes(hit.id)) selIds = [hit.id];
        UI.set({ selected: selIds });
        const starts = selIds.map(id => G.get().recs.obj[id]).filter((o): o is Obj => !!o && movable(o));
        starts.forEach(o => localLocks.add(o.id));
        drag.current = { kind: "move", sw: w, starts, last: 0, moved: false };
      } else if (e.pointerType === "touch") {
        UI.set({ selected: [] });
        const c = cam();
        drag.current = { kind: "pan", sx, sy, cx: c.x, cy: c.y, moved: false, right: false, w };
      } else {
        if (!e.shiftKey) UI.set({ selected: [] });
        drag.current = { kind: "marquee", x: w.x, y: w.y };
      }
      return;
    }
    if (tool === "ruler") {
      drag.current = { kind: "ruler", x: w.x, y: w.y, last: 0 };
      rulers.current.set("me", { pts: [w.x, w.y, w.x, w.y], shape: ui.rulerShape, color: myColor(), t: performance.now() });
      return;
    }
    if (tool === "draw") {
      if (ui.drawShape === "poly") {
        draft.current.push(w.x, w.y);
        preview.current = { kind: "poly", pts: [...draft.current, w.x, w.y] };
        invalidate();
        return;
      }
      drag.current = { kind: "draw", pts: [w.x, w.y, w.x, w.y] };
      return;
    }
    if (tool === "text") {
      addText(w.x, w.y);
      return;
    }
    if (tool === "fog" && gm) {
      if (ui.fogShape === "poly") {
        draft.current.push(w.x, w.y);
        preview.current = { kind: "fog", mode: ui.fogMode, pts: [...draft.current, w.x, w.y] };
        invalidate();
        return;
      }
      drag.current = { kind: "fogrect", x: w.x, y: w.y };
      return;
    }
    if (tool === "wall" && gm) {
      const sn = snapPoint(w, p, e.altKey);
      draft.current.push(sn.x, sn.y);
      preview.current = { kind: "wall", pts: [...draft.current, sn.x, sn.y] };
      invalidate();
      return;
    }
    if (tool === "door" && gm) {
      const sn = snapPoint(w, p, e.altKey);
      drag.current = { kind: "door", x: sn.x, y: sn.y };
      return;
    }
  };

  const snapPoint = (w: { x: number; y: number }, p: Page, alt: boolean) => {
    if (alt || !p.snap) return w;
    const g = p.grid / 2; // walls snap to half-grid
    return { x: Math.round(w.x / g) * g, y: Math.round(w.y / g) * g };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    if (e.pointerType === "touch" && touches.current.has(e.pointerId)) {
      touches.current.set(e.pointerId, { x: sx, y: sy });
      if (pinch.current && touches.current.size >= 2) {
        const [a, b] = [...touches.current.values()];
        const c = cam();
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        c.z = Math.max(0.08, Math.min(6, pinch.current.z * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.d)));
        c.x = pinch.current.wx - mx / c.z;
        c.y = pinch.current.wy - my / c.z;
        invalidate();
        return;
      }
    }
    const w = toWorld(sx, sy);
    const dm = Math.hypot(sx - mouse.current.sx, sy - mouse.current.sy);
    mouse.current = { x: w.x, y: w.y, sx, sy };
    const d = drag.current;
    const p = page();
    if (!p) return;
    const ui = UI.get();

    if (!d) {
      // hover feedback
      const door = doorHandleAt(pageObjs().filter(o => o.type === "wall"), w.x, w.y, cam().z);
      const hd = door && (isGM() || p.lighting) ? door.id : null;
      if (hd !== hoverDoor.current) { hoverDoor.current = hd; invalidate(); }
      let cursor = ui.tool === "pan" ? "grab" : ui.tool === "select" ? "default" : "crosshair";
      if (hd) cursor = "pointer";
      else if (ui.tool === "select") {
        const sel = ui.selected.length === 1 ? G.get().recs.obj[ui.selected[0]] : null;
        const hs = 9 / cam().z;
        const h = sel && movable(sel) && sel.type !== "wall" ? handles(sel).find(h => h.k !== "rot" && Math.abs(h.x - w.x) < hs && Math.abs(h.y - w.y) < hs) : null;
        if (h) cursor = ({ n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize", ne: "nesw-resize", sw: "nesw-resize", nw: "nwse-resize", se: "nwse-resize" } as any)[h.k];
        else if (topAt(w.x, w.y)) cursor = "move";
      }
      canvas.current!.style.cursor = cursor;
      if (draft.current.length) {
        const sn = ui.tool === "wall" ? snapPoint(w, p, e.altKey) : w;
        if (preview.current) preview.current = { ...preview.current, pts: [...draft.current, sn.x, sn.y] };
        invalidate();
      }
      return;
    }
    if (dm > 4) clearTimeout(longPress.current);

    switch (d.kind) {
      case "pan": {
        if (Math.hypot(sx - d.sx, sy - d.sy) > 4) d.moved = true;
        const c = cam();
        c.x = d.cx - (sx - d.sx) / c.z;
        c.y = d.cy - (sy - d.sy) / c.z;
        invalidate();
        break;
      }
      case "move": {
        const dx = w.x - d.sw.x, dy = w.y - d.sw.y;
        if (!d.moved && Math.hypot(dx, dy) * cam().z < 3) return;
        d.moved = true;
        for (const s of d.starts) {
          const cur = G.get().recs.obj[s.id];
          if (!cur) continue;
          if (s.type === "wall") setLocal({ ...cur, pts: (s.pts || []).map((v, i) => v + (i % 2 ? dy : dx)) });
          else setLocal({ ...cur, x: s.x + dx, y: s.y + dy });
        }
        const lead = d.starts.find(o => o.type === "image");
        if (lead) {
          const c0 = center(lead);
          rulers.current.set("me", { pts: [c0.x, c0.y, c0.x + dx, c0.y + dy], shape: "line", color: myColor(), t: performance.now() });
        }
        const now = performance.now();
        if (now - d.last > 70) {
          d.last = now;
          for (const s of d.starts) {
            const cur = G.get().recs.obj[s.id];
            if (cur) send({ t: "update", col: "obj", id: s.id, patch: s.type === "wall" ? { pts: cur.pts } : { x: cur.x, y: cur.y } });
          }
        }
        break;
      }
      case "resize": {
        const s = d.start;
        const l = toLocal(s, w.x, w.y);
        let x1 = 0, y1 = 0, x2 = s.w, y2 = s.h;
        if (d.h.includes("e")) x2 = l.x;
        if (d.h.includes("w")) x1 = l.x;
        if (d.h.includes("s")) y2 = l.y;
        if (d.h.includes("n")) y1 = l.y;
        if ((e.shiftKey || s.type === "text" || s.type === "card") && d.h.length === 2) {
          const ratio = s.w / s.h;
          const nw = x2 - x1, nh = y2 - y1;
          if (Math.abs(nw / ratio) > Math.abs(nh)) { const t = nw / ratio; d.h.includes("n") ? (y1 = y2 - t) : (y2 = y1 + t); }
          else { const t = nh * ratio; d.h.includes("w") ? (x1 = x2 - t) : (x2 = x1 + t); }
        }
        const nw = Math.max(5, Math.abs(x2 - x1)), nh = Math.max(5, Math.abs(y2 - y1));
        const lcx = (Math.min(x1, x2) + Math.max(x1, x2)) / 2 - s.w / 2, lcy = (Math.min(y1, y2) + Math.max(y1, y2)) / 2 - s.h / 2;
        const a = ((s.rot || 0) * Math.PI) / 180;
        const c = center(s);
        const ncx = c.x + lcx * Math.cos(a) - lcy * Math.sin(a), ncy = c.y + lcx * Math.sin(a) + lcy * Math.cos(a);
        const next: Obj = { ...G.get().recs.obj[s.id], x: ncx - nw / 2, y: ncy - nh / 2, w: nw, h: nh };
        if (s.type === "text") next.fontSize = Math.max(6, Math.round((s.fontSize || 28) * (nh / s.h)));
        setLocal(next);
        break;
      }
      case "rotate": {
        const c = center(d.start);
        let ang = (Math.atan2(w.y - c.y, w.x - c.x) * 180) / Math.PI + 90;
        if (e.shiftKey) ang = Math.round(ang / 15) * 15;
        setLocal({ ...G.get().recs.obj[d.start.id], rot: Math.round(((ang % 360) + 360) % 360) });
        break;
      }
      case "vertex": {
        const sn = snapPoint(w, p, e.altKey);
        const pts = [...(G.get().recs.obj[d.start.id]?.pts || [])];
        pts[d.idx] = sn.x;
        pts[d.idx + 1] = sn.y;
        setLocal({ ...G.get().recs.obj[d.start.id], pts });
        break;
      }
      case "marquee":
        preview.current = { kind: "marquee", pts: [d.x, d.y, w.x, w.y] };
        invalidate();
        break;
      case "draw": {
        const shape = ui.drawShape;
        if (shape === "free") d.pts.push(w.x, w.y);
        else {
          let x2 = w.x, y2 = w.y;
          if (e.shiftKey && (shape === "rect" || shape === "ellipse")) {
            const s = Math.max(Math.abs(x2 - d.pts[0]), Math.abs(y2 - d.pts[1]));
            x2 = d.pts[0] + Math.sign(x2 - d.pts[0]) * s;
            y2 = d.pts[1] + Math.sign(y2 - d.pts[1]) * s;
          }
          d.pts = [d.pts[0], d.pts[1], x2, y2];
        }
        preview.current = { kind: shape === "free" ? "path" : shape === "line" ? "line" : (shape as any), pts: d.pts };
        invalidate();
        break;
      }
      case "ruler": {
        const pts = [d.x, d.y, w.x, w.y];
        rulers.current.set("me", { pts, shape: ui.rulerShape, color: myColor(), t: performance.now() });
        const now = performance.now();
        if (now - d.last > 80) { d.last = now; broadcastRuler(pts, ui.rulerShape); }
        invalidate();
        break;
      }
      case "fogrect":
        preview.current = { kind: "fog", mode: ui.fogMode, pts: [d.x, d.y, w.x, d.y, w.x, w.y, d.x, w.y] };
        invalidate();
        break;
      case "door": {
        const sn = snapPoint(w, p, e.altKey);
        preview.current = { kind: "wall", door: true, pts: [d.x, d.y, sn.x, sn.y] };
        invalidate();
        break;
      }
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    clearTimeout(longPress.current);
    if (e.pointerType === "touch") {
      touches.current.delete(e.pointerId);
      if (pinch.current) {
        if (touches.current.size < 2) pinch.current = null;
        drag.current = null;
        return;
      }
    }
    const d = drag.current;
    drag.current = null;
    const p = page();
    if (!d || !p) return;
    const r = canvas.current!.getBoundingClientRect();
    const w = toWorld(e.clientX - r.left, e.clientY - r.top);
    const ui = UI.get();
    const gm = isGM();

    switch (d.kind) {
      case "pan":
        if (d.right && !d.moved) {
          const hit = topAt(d.w.x, d.w.y);
          if (hit && !ui.selected.includes(hit.id)) UI.set({ selected: [hit.id] });
          setMenu({ x: e.clientX, y: e.clientY, id: hit?.id ?? null, wx: d.w.x, wy: d.w.y });
        }
        break;
      case "move": {
        rulers.current.delete("me");
        const entry: UndoEntry = [];
        for (const s of d.starts) {
          const cur = G.get().recs.obj[s.id];
          if (!cur) continue;
          if (!d.moved) continue;
          if (s.type === "wall") {
            update("obj", s.id, { pts: cur.pts });
          } else {
            const sn = e.altKey ? { x: cur.x, y: cur.y } : snapObj(cur, p);
            update("obj", s.id, { x: sn.x, y: sn.y });
          }
          entry.push({ id: s.id, before: s, after: cur });
        }
        if (entry.length) pushUndo(entry);
        const ids = d.starts.map(s => s.id);
        setTimeout(() => ids.forEach(id => localLocks.delete(id)), 400);
        invalidate();
        break;
      }
      case "resize":
      case "rotate":
      case "vertex": {
        const cur = G.get().recs.obj[d.start.id];
        if (cur) {
          let patch: any = { x: cur.x, y: cur.y, w: cur.w, h: cur.h, rot: cur.rot };
          if (d.kind === "vertex") patch = { pts: cur.pts };
          if (d.kind === "resize" && cur.type === "text") patch.fontSize = cur.fontSize;
          if (d.kind === "resize" && cur.type === "image" && p.snap && !e.altKey) {
            const g = p.grid / 2;
            const nw = Math.max(g, Math.round(cur.w / g) * g), nh = Math.max(g, Math.round(cur.h / g) * g);
            const c = center(cur);
            Object.assign(patch, { w: nw, h: nh, x: c.x - nw / 2, y: c.y - nh / 2 });
            const sn = snapObj({ ...cur, ...patch }, p);
            patch.x = sn.x;
            patch.y = sn.y;
          }
          update("obj", cur.id, patch);
          pushUndo([{ id: cur.id, before: d.start, after: { ...cur, ...patch } }]);
        }
        setTimeout(() => localLocks.delete(d.start.id), 400);
        break;
      }
      case "marquee": {
        preview.current = null;
        const x1 = Math.min(d.x, w.x), x2 = Math.max(d.x, w.x), y1 = Math.min(d.y, w.y), y2 = Math.max(d.y, w.y);
        if (x2 - x1 > 2 / cam().z || y2 - y1 > 2 / cam().z) {
          const ids = pageObjs().filter(o => canPick(o)).filter(o => {
            const b = objBounds(o);
            return b.x2 >= x1 && b.x1 <= x2 && b.y2 >= y1 && b.y1 <= y2;
          }).map(o => o.id);
          UI.set({ selected: e.shiftKey ? [...new Set([...ui.selected, ...ids])] : ids });
        }
        invalidate();
        break;
      }
      case "draw": {
        preview.current = null;
        const shape = ui.drawShape;
        const layer = gm ? (ui.layer === "walls" ? "objects" : ui.layer) : "objects";
        const base = { pageId: p.id, layer, stroke: ui.stroke, fill: ui.fill, strokeWidth: ui.strokeWidth, z: Date.now() };
        if (shape === "free" || shape === "line") {
          const pts = shape === "free" ? simplify(d.pts, 1.5 / cam().z) : d.pts;
          if (pts.length < 4 || (Math.abs(pts[0] - pts[pts.length - 2]) < 2 && Math.abs(pts[1] - pts[pts.length - 1]) < 2 && pts.length <= 4)) break;
          createObj({ ...base, type: "path", ...pathFromPoints(pts), fill: shape === "line" ? "transparent" : base.fill });
        } else {
          const [x1, y1, x2, y2] = d.pts;
          if (Math.abs(x2 - x1) < 3 || Math.abs(y2 - y1) < 3) break;
          createObj({ ...base, type: shape as "rect" | "ellipse", x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) });
        }
        invalidate();
        break;
      }
      case "ruler":
        rulers.current.delete("me");
        broadcastRuler(null);
        invalidate();
        break;
      case "fogrect": {
        preview.current = null;
        const x1 = Math.min(d.x, w.x), x2 = Math.max(d.x, w.x), y1 = Math.min(d.y, w.y), y2 = Math.max(d.y, w.y);
        if (x2 - x1 > 3 && y2 - y1 > 3) addFog([x1, y1, x2, y1, x2, y2, x1, y2]);
        invalidate();
        break;
      }
      case "door": {
        preview.current = null;
        const sn = snapPoint(w, p, e.altKey);
        if (Math.hypot(sn.x - d.x, sn.y - d.y) > 5) createObj({ type: "wall", layer: "walls", pageId: p.id, x: 0, y: 0, w: 0, h: 0, pts: [d.x, d.y, sn.x, sn.y], door: true, open: false });
        invalidate();
        break;
      }
    }
  };

  const createObj = async (data: Partial<Obj>) => {
    const id = await create("obj", data);
    const rec = G.get().recs.obj[id];
    pushUndo([{ id, before: null, after: rec ?? (data as Obj) }]);
    return id;
  };

  const addFog = (pts: number[]) => {
    const p = page();
    if (!p) return;
    const mode = UI.get().fogMode;
    update("page", p.id, { fog: [...(p.fog || []), { mode, pts: pts.map(v => Math.round(v)) }], fogEnabled: true });
  };

  const finishDraft = () => {
    const pts = draft.current;
    const p = page();
    const ui = UI.get();
    draft.current = [];
    preview.current = null;
    invalidate();
    if (!p) return;
    if (ui.tool === "wall" && pts.length >= 4) createObj({ type: "wall", layer: "walls", pageId: p.id, x: 0, y: 0, w: 0, h: 0, pts });
    else if (ui.tool === "fog" && pts.length >= 6) addFog(pts);
    else if (ui.tool === "draw" && pts.length >= 6) {
      const layer = isGM() ? (ui.layer === "walls" ? "objects" : ui.layer) : "objects";
      createObj({ type: "path", pageId: p.id, layer, stroke: ui.stroke, fill: ui.fill, strokeWidth: ui.strokeWidth, ...pathFromPoints([...pts, pts[0], pts[1]]) });
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const ui = UI.get();
    if (draft.current.length) {
      // the dblclick's two clicks each added a point — drop the duplicate
      const d = draft.current;
      if (d.length >= 4 && Math.abs(d[d.length - 2] - d[d.length - 4]) < 3 && Math.abs(d[d.length - 1] - d[d.length - 3]) < 3) d.splice(-2, 2);
      finishDraft();
      return;
    }
    if (ui.tool !== "select") return;
    const r = canvas.current!.getBoundingClientRect();
    const w = toWorld(e.clientX - r.left, e.clientY - r.top);
    const hit = topAt(w.x, w.y);
    if (!hit) return;
    if (hit.type === "text" && controlsObj(hit)) editText(hit);
    else if (hit.type === "card" && controlsObj(hit)) update("obj", hit.id, { faceUp: !hit.faceUp });
    else if (hit.type === "image" && controlsObj(hit)) openWindow("token", hit.id);
    else if (hit.represents) openWindow("char", hit.represents);
  };

  const addText = async (x: number, y: number) => {
    const t = await prompt("Text");
    if (!t) return;
    const ui = UI.get();
    const p = page();
    if (!p) return;
    const m = measureText(t, ui.fontSize);
    const layer = isGM() ? (ui.layer === "walls" ? "objects" : ui.layer) : "objects";
    createObj({ type: "text", pageId: p.id, layer, x, y, w: m.w, h: m.h, text: t, fontSize: ui.fontSize, stroke: ui.stroke === "transparent" ? "#000" : ui.stroke });
  };
  const editText = async (o: Obj) => {
    const t = await prompt("Edit text", o.text || "");
    if (t === null) return;
    const m = measureText(t, o.fontSize || 28);
    update("obj", o.id, { text: t, w: m.w, h: m.h });
  };

  // ---- wheel zoom / trackpad pan ----
  useEffect(() => {
    const cv = canvas.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const c = cam();
      const r = cv.getBoundingClientRect();
      const sx = e.clientX - r.left, sy = e.clientY - r.top;
      const trackpadPan = !e.ctrlKey && e.deltaMode === 0 && (Math.abs(e.deltaX) > 0 || !Number.isInteger(e.deltaY)) && Math.abs(e.deltaY) < 60;
      if (trackpadPan) {
        c.x += e.deltaX / c.z;
        c.y += e.deltaY / c.z;
      } else {
        const wx = c.x + sx / c.z, wy = c.y + sy / c.z;
        const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
        c.z = Math.max(0.08, Math.min(6, c.z * factor));
        c.x = wx - sx / c.z;
        c.y = wy - sy / c.z;
      }
      invalidate();
    };
    cv.addEventListener("wheel", onWheel, { passive: false });
    return () => cv.removeEventListener("wheel", onWheel);
  }, []);

  // ---- keyboard ----
  useEffect(() => {
    const typing = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      return t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if (typing(e)) return;
      const ui = UI.get();
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === " ") { spaceDown.current = true; return; }
      if (e.key === "Escape") {
        if (draft.current.length) { draft.current = []; preview.current = null; invalidate(); }
        else UI.set({ selected: [] });
        setMenu(null);
        return;
      }
      if (e.key === "Enter" && draft.current.length) { finishDraft(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && ui.selected.length) {
        e.preventDefault();
        deleteSelected();
        return;
      }
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); return; }
      if (mod && e.key.toLowerCase() === "c") { copySel(); return; }
      if (mod && e.key.toLowerCase() === "v") { e.preventDefault(); paste(); return; }
      if (mod && e.key.toLowerCase() === "a") {
        e.preventDefault();
        UI.set({ selected: pageObjs().filter(canPick).map(o => o.id) });
        return;
      }
      if (e.key.startsWith("Arrow") && ui.selected.length) {
        e.preventDefault();
        const p = page()!;
        const step = e.shiftKey ? 1 : p.grid;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        for (const id of ui.selected) {
          const o = G.get().recs.obj[id];
          if (!o || !movable(o)) continue;
          if (o.type === "wall") update("obj", id, { pts: (o.pts || []).map((v, i) => v + (i % 2 ? dy : dx)) });
          else update("obj", id, { x: o.x + dx, y: o.y + dy });
        }
        return;
      }
      if (!mod && !e.altKey) {
        const map: Record<string, Tool> = { v: "select", h: "pan", r: "ruler", d: "draw", t: "text" };
        const t = map[e.key.toLowerCase()];
        if (t) UI.set({ tool: t });
      }
    };
    const up = (e: KeyboardEvent) => { if (e.key === " ") spaceDown.current = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  const deleteSelected = () => {
    const ui = UI.get();
    const entry: UndoEntry = [];
    for (const id of ui.selected) {
      const o = G.get().recs.obj[id];
      if (!o) continue;
      if (!isGM() && o.owner !== G.get().me) continue;
      entry.push({ id, before: o, after: null });
      remove("obj", id);
    }
    if (entry.length) pushUndo(entry);
    UI.set({ selected: [] });
  };
  const copySel = () => {
    clipboard = UI.get().selected.map(id => G.get().recs.obj[id]).filter(Boolean).map(o => ({ ...o }));
  };
  const paste = async () => {
    if (!clipboard.length) return;
    const p = page();
    if (!p) return;
    const b = clipboard.map(objBounds);
    const minX = Math.min(...b.map(x => x.x1)), minY = Math.min(...b.map(x => x.y1));
    const ids: string[] = [];
    for (const o of clipboard) {
      if (!isGM() && !["path", "rect", "ellipse", "text"].includes(o.type)) continue;
      const { id: _id, ...data } = o;
      const dx = mouse.current.x - minX, dy = mouse.current.y - minY;
      if (o.type === "wall") (data as any).pts = (o.pts || []).map((v, i) => v + (i % 2 ? dy : dx));
      else { data.x = o.x + dx; data.y = o.y + dy; }
      data.pageId = p.id;
      data.z = Date.now() + ids.length;
      ids.push(await createObj(data));
    }
    UI.set({ selected: ids });
  };

  // ---- drag & drop from panels / desktop ----
  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const p = page();
    if (!p) return;
    const r = canvas.current!.getBoundingClientRect();
    const w = toWorld(e.clientX - r.left, e.clientY - r.top);
    const dt = e.dataTransfer;
    const gm = isGM();
    const card = dt.getData("application/x-hearth-card");
    if (card) {
      const { deckId, idx } = JSON.parse(card);
      send({ t: "deck", id: deckId, op: "play", idx, x: w.x - 50, y: w.y - 70, pageId: p.id, faceUp: !e.altKey });
      return;
    }
    if (!gm) return toast("Only the GM can place art and tokens");
    const layer: Layer = UI.get().layer === "walls" ? "objects" : UI.get().layer;
    const asset = dt.getData("application/x-hearth-asset");
    const ch = dt.getData("application/x-hearth-char");
    if (ch) return placeCharToken(ch, w.x, w.y, p);
    let src: string | null = null, name = "";
    if (asset) ({ url: src, name } = JSON.parse(asset));
    else if (dt.files.length) {
      const f = dt.files[0];
      if (!f.type.startsWith("image/")) return toast("Drop an image file");
      try {
        toast("Uploading…");
        const a = await uploadFile(f);
        src = a.url;
        name = a.name;
        window.dispatchEvent(new Event("hearth:assets"));
      } catch (err: any) { return toast(err.message); }
    } else {
      const url = dt.getData("text/uri-list") || dt.getData("text/plain");
      if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?\S*)?$/i.test(url)) src = url;
    }
    if (!src) return;
    placeImage(src, name.replace(/\.[a-z0-9]+$/i, ""), w.x, w.y, p, layer);
  };

  const placeImage = (src: string, name: string, x: number, y: number, p: Page, layer: Layer) => {
    const im = new Image();
    im.onload = () => {
      let w = p.grid, h = p.grid;
      if (layer === "map") {
        w = im.naturalWidth; h = im.naturalHeight;
      } else if (im.naturalWidth && im.naturalHeight) {
        const ar = im.naturalWidth / im.naturalHeight;
        if (ar > 1.05) h = Math.round(w / ar);
        else if (ar < 0.95) w = Math.round(h * ar);
      }
      let o: Partial<Obj> = { type: "image", src, name, layer, pageId: p.id, x: x - w / 2, y: y - h / 2, w, h, z: Date.now() };
      if (layer !== "map") o = { ...o, ...snapObj(o as Obj, p) };
      else if (p.snap) o = { ...o, x: Math.round(o.x! / p.grid) * p.grid, y: Math.round(o.y! / p.grid) * p.grid };
      createObj(o).then(id => UI.set({ selected: [id], layer }));
    };
    im.onerror = () => toast("Couldn't load that image");
    im.src = src;
  };

  const placeCharToken = (charId: string, x: number, y: number, p: Page) => {
    const ch = G.get().recs.char[charId];
    if (!ch) return;
    const dt = ch.defaultToken || {};
    const w = dt.w || p.grid, h = dt.h || p.grid;
    const npc = ch.attrs?.npc === "1";
    const sheet5e = ch.sheet === "dnd5e";
    const o: Partial<Obj> = {
      type: "image", layer: "objects", pageId: p.id, src: ch.avatar, name: ch.name, showName: true, represents: ch.id,
      bars: [
        { v: ch.attrs?.hp ?? "", max: ch.attrs?.hp_max ?? "", link: sheet5e && !npc ? "hp" : undefined },
        { v: ch.attrs?.ac ?? "", max: "", link: undefined },
        { v: "", max: "" },
      ],
      vision: !npc, darkvision: parseFloat(ch.attrs?.darkvision || "0") || 0,
      ...dt, x: x - w / 2, y: y - h / 2, w, h, z: Date.now(), id: undefined as any,
    };
    // mooks (NPCs) keep independent HP per token, unlinked
    if (npc && sheet5e && !dt.bars) o.bars = [{ v: ch.attrs?.hp ?? "", max: ch.attrs?.hp_max ?? ch.attrs?.hp ?? "" }, { v: ch.attrs?.ac ?? "", max: "" }, { v: "", max: "" }];
    delete (o as any).id;
    const sn = snapObj(o as Obj, p);
    createObj({ ...o, ...sn }).then(id => UI.set({ selected: [id], layer: "objects" }));
  };

  // ---- context menu actions ----
  const act = async (a: string, arg?: any) => {
    const m = menu;
    setMenu(null);
    const ids = UI.get().selected;
    const objs = ids.map(id => G.get().recs.obj[id]).filter(Boolean) as Obj[];
    const p = page();
    if (!p) return;
    switch (a) {
      case "front": objs.forEach((o, i) => update("obj", o.id, { z: Date.now() + i })); break;
      case "back": {
        const minZ = Math.min(...pageObjs().map(o => o.z));
        objs.forEach((o, i) => update("obj", o.id, { z: minZ - objs.length + i }));
        break;
      }
      case "layer":
        pushUndo(objs.map(o => ({ id: o.id, before: o, after: { ...o, layer: arg } })));
        objs.forEach(o => update("obj", o.id, { layer: arg }));
        UI.set({ selected: [] });
        break;
      case "edit": if (objs[0]) openWindow("token", objs[0].id); break;
      case "sheet": if (objs[0]?.represents) openWindow("char", objs[0].represents); break;
      case "turn": {
        for (const o of objs) send({ t: "turnAdd", tokenId: o.id, init: "0" });
        UI.set({ turnOpen: true });
        break;
      }
      case "delete": deleteSelected(); break;
      case "copy": copySel(); break;
      case "paste": paste(); break;
      case "lock": objs.forEach(o => update("obj", o.id, { locked: !o.locked })); break;
      case "flip": objs.forEach(o => update("obj", o.id, o.type === "card" ? { faceUp: !o.faceUp } : { flipH: !o.flipH })); break;
      case "take": objs.forEach(o => o.type === "card" && send({ t: "deck", id: o.deckId!, op: "take", objId: o.id })); break;
      case "fit": {
        const o = objs[0];
        if (o) update("obj", o.id, { x: 0, y: 0, w: p.width * p.grid, h: p.height * p.grid, rot: 0 });
        break;
      }
      case "fitgrid": {
        const o = objs[0];
        if (!o) break;
        const cells = await prompt("How many grid cells wide is this map image?", String(Math.round(o.w / p.grid)));
        const n = parseFloat(cells || "");
        if (!(n > 0)) break;
        const s = (n * p.grid) / o.w;
        update("obj", o.id, { w: o.w * s, h: o.h * s, x: 0, y: 0 });
        break;
      }
      case "name": objs.forEach(o => update("obj", o.id, { showName: !o.showName })); break;
      case "ping":
        if (m) {
          const fx = { kind: "ping", pageId: p.id, x: m.wx, y: m.wy, color: myColor() };
          send({ t: "fx", fx });
          pings.current.push({ ...fx, t0: performance.now() });
        }
        break;
      case "pull":
        if (m) {
          const fx = { kind: "pull", pageId: p.id, x: m.wx, y: m.wy, color: myColor() };
          send({ t: "fx", fx });
          pings.current.push({ ...fx, t0: performance.now() });
        }
        break;
      case "status": {
        const o = objs[0];
        if (!o) break;
        const st = o.statuses || [];
        const has = st.find(s => s.split("@")[0] === arg);
        objs.forEach(x => update("obj", x.id, { statuses: has ? (x.statuses || []).filter(s => s.split("@")[0] !== arg) : [...(x.statuses || []), arg] }));
        break;
      }
    }
  };

  const gm = isGM();
  const menuObj = menu?.id ? G.get().recs.obj[menu.id] : null;
  const sel = UI.get().selected.map(id => G.get().recs.obj[id]).filter(Boolean) as Obj[];

  return (
    <div
      ref={wrap}
      className="board"
      onDragOver={e => e.preventDefault()}
      onDrop={onDrop}
      onContextMenu={e => e.preventDefault()}
    >
      <canvas
        ref={canvas}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        tabIndex={0}
      />
      <ZoomControls cam={cam} invalidate={invalidate} size={size} page={page} />
      {menu && (
        <div
          className="ctx"
          style={{ left: menu.x, top: menu.y }}
          onPointerDown={e => e.stopPropagation()}
          ref={el => {
            if (!el) return;
            const r = el.getBoundingClientRect();
            if (r.bottom > window.innerHeight - 4) el.style.top = Math.max(4, window.innerHeight - r.height - 4) + "px";
            if (r.right > window.innerWidth - 4) el.style.left = Math.max(4, window.innerWidth - r.width - 4) + "px";
            // open submenus leftwards when there's no room on the right
            el.classList.toggle("flip", r.right + 200 > window.innerWidth);
          }}
        >
          {menuObj ? (
            <>
              {menuObj.type === "image" && controlsObj(menuObj) && <button onClick={() => act("edit")}>⚙ Edit token…</button>}
              {menuObj.represents && <button onClick={() => act("sheet")}>📜 Open sheet</button>}
              {(menuObj.type === "image") && controlsObj(menuObj) && <button onClick={() => act("turn")}>⏱ Add to turn order</button>}
              {menuObj.type === "image" && controlsObj(menuObj) && (
                <div className="ctx-sub">
                  <span>☺ Status markers ▸</span>
                  <div className="ctx-grid">
                    {["red", "blue", "green", "yellow", "purple", "dead", "prone", "poisoned", "stunned", "unconscious", "frightened", "restrained", "blinded", "charmed", "invisible", "concentrating", "blessed", "grappled", "paralyzed", "burning", "bleeding", "hidden", "flying", "shielded"].map(s => (
                      <button key={s} title={s} className={menuObj.statuses?.some(x => x.split("@")[0] === s) ? "on" : ""} onClick={() => act("status", s)}>
                        {s in STATUS_ICONS ? STATUS_ICONS[s] : "●"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {controlsObj(menuObj) && menuObj.type === "image" && <button onClick={() => act("name")}>{menuObj.showName ? "Hide" : "Show"} nameplate</button>}
              {menuObj.type === "card" && controlsObj(menuObj) && <><button onClick={() => act("flip")}>🂠 Flip card</button><button onClick={() => act("take")}>✋ Take to hand</button></>}
              {gm && (
                <>
                  <div className="ctx-sep" />
                  <button onClick={() => act("front")}>⤒ To front</button>
                  <button onClick={() => act("back")}>⤓ To back</button>
                  <div className="ctx-sub">
                    <span>☰ Layer ▸</span>
                    <div>
                      {(["map", "objects", "gm", "walls"] as Layer[]).filter(l => l !== menuObj.layer && (l === "walls") === (menuObj.type === "wall")).map(l => (
                        <button key={l} onClick={() => act("layer", l)}>{LAYER_LABEL[l]}</button>
                      ))}
                    </div>
                  </div>
                  {menuObj.type === "image" && menuObj.layer === "map" && <><button onClick={() => act("fit")}>⛶ Stretch to page</button><button onClick={() => act("fitgrid")}>▦ Scale to grid…</button></>}
                  {menuObj.type === "image" && <button onClick={() => act("flip")}>⇋ Flip horizontal</button>}
                  <button onClick={() => act("lock")}>{menuObj.locked ? "🔓 Unlock" : "🔒 Lock"}</button>
                </>
              )}
              <div className="ctx-sep" />
              <button onClick={() => act("copy")}>Copy</button>
              {(gm || menuObj.owner === G.get().me) && <button className="danger" onClick={() => act("delete")}>Delete{sel.length > 1 ? ` (${sel.length})` : ""}</button>}
            </>
          ) : (
            <>
              <button onClick={() => act("ping")}>📍 Ping here</button>
              {gm && <button onClick={() => act("pull")}>🎯 Pull everyone here</button>}
              {clipboard.length > 0 && <button onClick={() => { mouse.current.x = menu.wx; mouse.current.y = menu.wy; act("paste"); }}>Paste</button>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_ICONS: Record<string, string> = {
  red: "🔴", blue: "🔵", green: "🟢", yellow: "🟡", purple: "🟣", dead: "💀", prone: "🛌", poisoned: "🤢", stunned: "😵",
  unconscious: "💤", frightened: "😱", restrained: "⛓️", blinded: "🙈", charmed: "💘", invisible: "👻", concentrating: "🧠",
  blessed: "✨", grappled: "✊", paralyzed: "⚡", burning: "🔥", bleeding: "🩸", hidden: "👤", flying: "🕊️", shielded: "🛡️",
};
export const LAYER_LABEL: Record<Layer, string> = { map: "🗺 Map & background", objects: "♟ Tokens & objects", gm: "👁 GM info overlay", walls: "🧱 Dynamic lighting" };

function ZoomControls({ cam, invalidate, size, page }: { cam: () => Cam; invalidate: () => void; size: any; page: () => Page | null }) {
  const zoom = (f: number) => {
    const c = cam();
    const cx = c.x + size.current.w / 2 / c.z, cy = c.y + size.current.h / 2 / c.z;
    c.z = Math.max(0.08, Math.min(6, c.z * f));
    c.x = cx - size.current.w / 2 / c.z;
    c.y = cy - size.current.h / 2 / c.z;
    invalidate();
  };
  const fit = () => {
    const p = page();
    if (!p) return;
    const c = cam();
    const pw = p.width * p.grid, ph = p.height * p.grid;
    c.z = Math.min(size.current.w / pw, size.current.h / ph) * 0.95;
    c.x = pw / 2 - size.current.w / 2 / c.z;
    c.y = ph / 2 - size.current.h / 2 / c.z;
    invalidate();
  };
  return (
    <div className="zoom">
      <button onClick={() => zoom(1.25)} title="Zoom in">＋</button>
      <button onClick={fit} title="Fit page">⛶</button>
      <button onClick={() => zoom(0.8)} title="Zoom out">－</button>
    </div>
  );
}

export { distToSeg };
